/**
 * React Query hooks for the M2 owner-billing surface (spec §3).
 *
 * A sibling of `useOwner.ts` sharing its conventions: the per-uid namespaced query
 * key, `useSignedInOwnerGate()` for `{ enabled, uid }`, a `useQueryClient()` handle,
 * and NO PII held in any cache (the billing state carries a status + dates + counts,
 * never card data or investor identity).
 *
 * Surfaces:
 *   • billingKeys                 — the per-uid billing-state cache key.
 *   • useOwnerBillingState()      — the owner's billing state. A `501` (Stripe
 *     deliberately off) is NOT retried; a `503` (transient) IS. Entitlement is read
 *     from the server's `isEntitled` only — never re-derived on the FE (R2/R3).
 *   • useCreateCheckoutSession()  — create a Stripe Checkout, then redirect. A `409
 *     checkout_conflict` auto-opens the portal instead; a `409 trial_alignment` is
 *     surfaced by the billing page's final-48h state (the mutation just rejects).
 *   • useCreatePortalSession()    — create a Stripe Billing-Portal session, redirect.
 *   • useBillingReturnHandler()   — on `?billing=success|canceled|portal_return`,
 *     invalidate billing + dashboard, strip the param, and (for success) poll state
 *     briefly while the Stripe webhook lands (never assert 'active' from the redirect).
 *     It exposes an EXPLICIT, always-settling `confirmationPhase` (polling → confirmed
 *     / timed_out) — re-invalidating the dashboard once the webhook lands and rendering
 *     an authoritative retry state on exhaustion, never a perpetual "Confirming…".
 *   • useOwnerViews(slug)         — Fork A listing-view analytics (the renewal signal).
 *
 * Redirects deliberately LEAVE the SPA (`window.location.assign`) — Stripe hosts the
 * Checkout + Portal, so no card data or PII ever touches the FE.
 */

import { useEffect, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ownerApi, OwnerBillingApiError } from '@/api/ownerApi';
import { useSignedInOwnerGate, ownerKeys } from '@/hooks/queries/useOwner';
import type {
  OwnerBillingSession,
  OwnerBillingStateResult,
  OwnerViewsAnalytics,
} from '@/types/owner';

const MINUTE = 60 * 1000;

/** Webhook-lag poll knobs: refetch every 3s up to 4 times while status !== 'active'. */
export const WEBHOOK_POLL_INTERVAL_MS = 3000;
export const WEBHOOK_POLL_MAX_ATTEMPTS = 4;

/**
 * Stable, per-uid billing query key (mirrors `ownerKeys.dashboard(uid)`). Per-uid
 * namespacing is mandatory: two users on one device must NEVER share a cached
 * billing state, and a sign-out (uid → null) parks under a distinct, never-fetched
 * key.
 */
export const billingKeys = {
  state: (uid: string | null) => ['owner', 'billing', 'state', uid] as const,
};

/**
 * The signed-in owner's billing state. Enabled for ANY signed-in user (the server
 * returns the `'none'` fallback for a non-claimant). Entitlement is the server's
 * `isEntitled`; the FE never re-derives it (spec §2.1).
 *
 * Retry policy is the load-bearing 501-vs-503 split (R2): a `501
 * owner_billing_not_configured` is a DELIBERATE fail-closed config state, not a
 * transient fault → NOT retried (so the "billing not enabled" panel settles at once,
 * never an infinite spinner). A `503` IS transient → the default capped retry.
 */
export function useOwnerBillingState() {
  const { enabled, uid } = useSignedInOwnerGate();
  return useQuery<OwnerBillingStateResult>({
    queryKey: billingKeys.state(uid),
    queryFn: () => ownerApi.getBillingState(),
    enabled,
    staleTime: MINUTE, // a trial day-countdown does not need to be second-fresh
    gcTime: 10 * MINUTE,
    retry: (failureCount, error) => {
      // 501 = deliberate fail-closed config → never retry (settle immediately).
      if (error instanceof OwnerBillingApiError && error.status === 501) {
        return false;
      }
      // Everything else (incl. 503 transient) → the default capped retry.
      return failureCount < 2;
    },
  });
}

/**
 * Owner-billing toast for a redirect-flow failure (spec §5 "Toasts"). A `409
 * checkout_conflict` is handled by the caller (auto-opens the portal) — this only
 * covers the transient case and any other hard error. Never surfaces PII.
 */
export function toastBillingError(error: unknown): void {
  if (
    error instanceof OwnerBillingApiError &&
    error.code === 'owner_billing_checkout_conflict'
  ) {
    // Handled by the mutation's onError (auto-opens the portal); no generic toast.
    return;
  }
  toast.error('Couldn’t start billing right now. Please try again in a moment.');
}

/**
 * Create a Stripe-hosted Checkout session, then redirect the browser to it. The
 * return URLs land back on the billing page so `useBillingReturnHandler` can refresh
 * entitlement. On a `409 checkout_conflict` (already subscribed) the owner is routed
 * to the portal instead. A `409 trial_alignment_unavailable` rejects without a toast
 * — the billing page's final-48h state explains it.
 *
 * `openPortal` (the portal mutation's `mutate`) is injected so the conflict path can
 * chain into it without this hook importing the other mutation and creating a cycle.
 */
export function useCreateCheckoutSession(openPortal?: () => void) {
  return useMutation<OwnerBillingSession, OwnerBillingApiError, void>({
    mutationFn: () =>
      ownerApi.createCheckoutSession({
        successUrl: `${window.location.origin}/owner/billing?billing=success`,
        cancelUrl: `${window.location.origin}/owner/billing?billing=canceled`,
      }),
    onSuccess: ({ url }) => {
      window.location.assign(url); // leave the SPA to Stripe-hosted Checkout
    },
    onError: (error) => {
      if (error.code === 'owner_billing_checkout_conflict') {
        toast.message('You already have an active subscription — opening your billing portal.');
        openPortal?.();
        return;
      }
      // The final-48h alignment conflict (`owner_billing_trial_alignment_unavailable`)
      // is surfaced as a PAGE STATE, not a toast: the mutation still rejects, so
      // `mutation.error` is populated, and `OwnerBilling` reads `startCheckout.error`
      // to render the honest final-48h "checkout blocked" hero (F1 — no longer a silent
      // dead button on a click the payload didn't predict). We suppress only the toast
      // here; we deliberately do NOT swallow the rejection.
      if (error.code === 'owner_billing_trial_alignment_unavailable') {
        return;
      }
      toastBillingError(error);
    },
  });
}

/**
 * Create a Stripe-hosted Billing-Portal session, then redirect. Used both for the
 * "Manage subscription" / "Update payment method" CTAs and as the conflict target of
 * the checkout mutation. No cache write is needed pre-redirect (the browser leaves).
 */
export function useCreatePortalSession() {
  return useMutation<OwnerBillingSession, OwnerBillingApiError, void>({
    mutationFn: () =>
      ownerApi.createPortalSession({
        returnUrl: `${window.location.origin}/owner/billing?billing=portal_return`,
      }),
    onSuccess: ({ url }) => {
      window.location.assign(url); // leave the SPA to the Stripe-hosted portal
    },
    onError: (error) => toastBillingError(error),
  });
}

/** The `?billing=` return states the billing page reacts to. */
export type BillingReturnParam = 'success' | 'canceled' | 'portal_return';

function parseBillingReturn(search: string): BillingReturnParam | null {
  const value = new URLSearchParams(search).get('billing');
  return value === 'success' || value === 'canceled' || value === 'portal_return'
    ? value
    : null;
}

/** The terminal outcome of the webhook-lag poll (drives the honest hero phase, F10). */
export type BillingPollOutcome = 'active' | 'timed_out';

/**
 * Refetch the billing state while the Stripe webhook has not yet flipped the status
 * to `active` — up to {@link WEBHOOK_POLL_MAX_ATTEMPTS} times, every
 * {@link WEBHOOK_POLL_INTERVAL_MS}. Entitlement flips on the webhook, which may land
 * a few seconds after the browser returns from Checkout, so `?billing=success` means
 * "payment submitted", never "active" — active is asserted ONLY from a refetch.
 *
 * The poll ALWAYS reaches a terminal outcome and reports it via `onResolved` exactly
 * ONCE (unless cancelled first): `'active'` the moment the status flips, or
 * `'timed_out'` when it exhausts every attempt without activation. The caller uses
 * that to leave the "Confirming…" phase — a sticky "success" ref that never settles is
 * the #10 defect (the hero would show "Confirming…" forever after exhaustion).
 *
 * Exported for direct unit testing (deterministic, injectable clock/queryClient).
 */
export function pollBillingUntilActive(
  queryClient: QueryClient,
  uid: string | null,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    setTimeoutFn?: typeof setTimeout;
    clearTimeoutFn?: typeof clearTimeout;
    /** Fired exactly once with the terminal outcome (never after cancel). */
    onResolved?: (outcome: BillingPollOutcome) => void;
  } = {},
): () => void {
  const intervalMs = options.intervalMs ?? WEBHOOK_POLL_INTERVAL_MS;
  const maxAttempts = options.maxAttempts ?? WEBHOOK_POLL_MAX_ATTEMPTS;
  const setTimeoutFn = options.setTimeoutFn ?? setTimeout;
  const clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout;
  const onResolved = options.onResolved;

  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;
  let resolved = false;

  // Report the terminal outcome at most once, and never after cancellation.
  const resolve = (outcome: BillingPollOutcome) => {
    if (resolved || cancelled) return;
    resolved = true;
    onResolved?.(outcome);
  };

  const tick = async () => {
    if (cancelled) return;
    const current = queryClient.getQueryData<OwnerBillingStateResult>(
      billingKeys.state(uid),
    );
    // Settle the moment the webhook has landed (status flipped to active).
    if (current?.status === 'active') {
      resolve('active');
      return;
    }
    if (attempts >= maxAttempts) {
      // Exhausted without activation → a definite timed-out outcome, never a
      // perpetual "Confirming…" (F10).
      resolve('timed_out');
      return;
    }
    attempts += 1;
    // Fetch fresh state directly and write it into the cache (observer-independent:
    // the poll must make progress even if the page's query observer is momentarily
    // absent). A transient failure is swallowed — the next tick retries.
    try {
      const next = await ownerApi.getBillingState();
      if (cancelled) return;
      queryClient.setQueryData(billingKeys.state(uid), next);
      if (next.status === 'active') {
        resolve('active');
        return;
      }
    } catch {
      if (cancelled) return;
    }
    timer = setTimeoutFn(() => void tick(), intervalMs);
  };

  timer = setTimeoutFn(() => void tick(), intervalMs);

  return () => {
    cancelled = true;
    if (timer !== null) clearTimeoutFn(timer);
  };
}

/**
 * The confirmation phase of a `?billing=success` return (F10). It replaces the old
 * sticky `returnParamRef` — which never settled, so the hero could show "Confirming…"
 * forever after the poll exhausted:
 *   • `'idle'`      — not a success return (canceled / portal_return / no param).
 *   • `'polling'`   — payment submitted; the webhook-lag poll is running (the honest
 *                     "Confirming your subscription…" interim).
 *   • `'confirmed'` — the poll saw the status flip to `active` (webhook landed).
 *   • `'timed_out'` — the poll exhausted every attempt without activation → render an
 *                     authoritative "still confirming / refresh" state, NEVER a
 *                     permanent busy spinner.
 */
export type BillingConfirmationPhase = 'idle' | 'polling' | 'confirmed' | 'timed_out';

/**
 * Billing-return handler for the billing page (spec §3.4). When
 * `?billing=success|canceled|portal_return` is present on mount it:
 *   1. invalidates `billingKeys.state(uid)` (fresh entitlement) and
 *      `ownerKeys.dashboard(uid)` (gated CTAs may have flipped trial→active);
 *   2. strips the `?billing=` param (`replace`) so a refresh doesn't re-toast; and
 *   3. for `success`, kicks off the webhook-lag poll (never asserts 'active' from
 *      the redirect alone) and tracks an EXPLICIT confirmation phase.
 *
 * Returns the parsed `returnParam` (before stripping) AND the explicit
 * `confirmationPhase` so the page renders an honest, ALWAYS-SETTLING interim: the
 * "Confirming…" hero belongs to the `'polling'` phase ONLY. When the poll reaches
 * `active`, the phase flips to `'confirmed'` and `ownerKeys.dashboard(uid)` is
 * re-invalidated so the gated CTAs pick up the freshly-active entitlement (the first
 * invalidation fired BEFORE the webhook, so caps would otherwise stay stale up to 2
 * minutes — #10). On exhaustion the phase flips to `'timed_out'` so the hero shows an
 * authoritative retry state, never a perpetual "Confirming…".
 *
 * F2 — the poll must SURVIVE the param-strip. The consume/navigate concern and the
 * poll-lifecycle concern are split into two effects on purpose:
 *   • Effect A (deps include `location.search`) consumes the param ONCE (guarded by
 *     `handledRef`), invalidates, strips the param via `navigate(replace)`, and — for
 *     `success` — sets the phase to `'polling'` and bumps a stable `pollGeneration`.
 *     `navigate` synchronously changes `location.search`, so this effect re-runs;
 *     because it OWNS no poll canceller, that benign re-run tears nothing down.
 *   • Effect B is keyed on `[pollGeneration, uid]` (NOT `location.search`), so the
 *     param strip does not re-run it. It owns the poll's whole lifecycle and cancels
 *     ONLY on unmount / a new generation — the earlier bug (canceller returned from
 *     the search-keyed effect, torn down before the first 3s tick) cannot recur.
 */
export function useBillingReturnHandler(): {
  returnParam: BillingReturnParam | null;
  confirmationPhase: BillingConfirmationPhase;
} {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  const location = useLocation();
  const navigate = useNavigate();

  // Capture the return param ONCE per arrival (before we strip it from the URL), so
  // the page can keep reasoning about the arrival after the param is gone.
  const returnParamRef = useRef<BillingReturnParam | null>(
    parseBillingReturn(location.search),
  );

  const handledRef = useRef(false);
  // A monotonically-incrementing token: each `success` return bumps it, and Effect B
  // (which owns the poll) is keyed on it. Kept as state (not a ref) so a bump actually
  // re-runs Effect B; a ref mutation would not schedule the poll.
  const [pollGeneration, setPollGeneration] = useState(0);
  // The EXPLICIT, always-settling confirmation phase (F10) — never a sticky ref.
  const [confirmationPhase, setConfirmationPhase] =
    useState<BillingConfirmationPhase>('idle');

  // Effect A — consume the param exactly once, invalidate, strip, and trigger the poll.
  useEffect(() => {
    const param = parseBillingReturn(location.search);
    if (param === null || handledRef.current) {
      return;
    }
    handledRef.current = true;
    returnParamRef.current = param;

    void queryClient.invalidateQueries({ queryKey: billingKeys.state(uid) });
    void queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) });

    // Strip `?billing=` so a refresh doesn't re-trigger the handler. This synchronously
    // changes `location.search`, re-running THIS effect — but it returns no cleanup, so
    // nothing is torn down. The poll lives in Effect B, keyed off `pollGeneration`.
    navigate(location.pathname, { replace: true });

    if (param === 'success') {
      setConfirmationPhase('polling');
      setPollGeneration((g) => g + 1);
    }
  }, [location.pathname, location.search, navigate, queryClient, uid]);

  // Effect B — own the webhook-lag poll. Keyed on `[pollGeneration, uid]` ONLY, so the
  // param-strip re-run of Effect A never cancels it. Cancels on unmount (or a new
  // generation). `pollGeneration === 0` = no success return yet → no poll.
  useEffect(() => {
    if (pollGeneration === 0) {
      return undefined;
    }
    return pollBillingUntilActive(queryClient, uid, {
      onResolved: (outcome) => {
        if (outcome === 'active') {
          // The webhook landed. Re-invalidate the dashboard so the gated CTAs pick up
          // the now-active entitlement — the pre-webhook invalidation in Effect A read
          // stale caps that would otherwise persist up to 2 minutes (#10).
          void queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) });
          void queryClient.invalidateQueries({ queryKey: billingKeys.state(uid) });
          setConfirmationPhase('confirmed');
        } else {
          // Exhausted without activation → an authoritative retry state, never a
          // permanent "Confirming…" busy hero (#10).
          setConfirmationPhase('timed_out');
        }
      },
    });
  }, [pollGeneration, queryClient, uid]);

  return { returnParam: returnParamRef.current, confirmationPhase };
}

// ── Fork A: owner listing-view analytics (the renewal signal) ─────────────────

/**
 * The owner's listing-view analytics for one raise (`ownerApi.getViewsAnalytics`).
 * Keyed per slug (analytics are a raise-scoped read, authorized server-side by the
 * isOwner gate — not per-uid, matching `ownerKeys.capitalStack`). Enabled only for a
 * present slug. Deliberately NOT billing-gated (the renewal signal must stay free).
 */
export function useOwnerViews(slug: string | null | undefined) {
  const { enabled } = useSignedInOwnerGate();
  const hasSlug = typeof slug === 'string' && slug.length > 0;
  return useQuery<OwnerViewsAnalytics>({
    queryKey: ['owner', 'views', slug ?? null] as const,
    queryFn: () => ownerApi.getViewsAnalytics(slug as string),
    enabled: enabled && hasSlug,
    staleTime: 5 * MINUTE, // view counts move slowly; no need to be second-fresh
    gcTime: 10 * MINUTE,
  });
}

/** Convenience: whether the billing result is the non-claimant `'none'` fallback. */
export function isNoneBillingState(
  state: OwnerBillingStateResult | undefined,
): boolean {
  return state?.status === 'none';
}

/**
 * The billing-page manual Refresh/Retry handler (round-7 #28). It refetches the
 * billing STATE and — the load-bearing part — when that manual refetch FIRST observes
 * `status === 'active'`, it ALSO invalidates the owner dashboard (`ownerCapabilities`)
 * + the billing-state cache so the gated CTAs enable in step with the now-active
 * subscription.
 *
 * The defect it closes: the "still activating" / error hero's Refresh only refetched
 * the billing state, so the hero flipped to "Active" while every gated capability
 * stayed stale-disabled — the caps were only refreshed by the automatic post-checkout
 * poll (`useBillingReturnHandler`), never by THIS manual path. A user who let the poll
 * time out, then hit Refresh after the webhook landed, saw "Active" with dead CTAs.
 *
 * Owning the queryClient + uid inside this hook (rather than in the page) keeps the
 * page thin and testable, mirroring the other billing hooks in this module.
 */
export function useBillingRefreshWithCapabilitySync(): () => Promise<void> {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  const query = useOwnerBillingState();
  return async () => {
    const result = await query.refetch();
    if (result.data?.status === 'active') {
      void queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) });
      void queryClient.invalidateQueries({ queryKey: billingKeys.state(uid) });
    }
  };
}
